import { sniffImage, type PixelBuffer, type SniffResult } from '@photovalidator/core';

/**
 * Bitmap helpers.
 *
 * Everything here is about *reading* pixels, never about measurements or edits:
 * the core decides what the numbers mean, and the validator never modifies the
 * user's photo. There is deliberately no rotate/crop/resize/export code — that
 * is the job of the host editor (ГОСТ ФОТО).
 */

export type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;
export type AnyContext = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

export interface CanvasHandle {
  canvas: AnyCanvas;
  ctx: AnyContext;
}

export const supportsOffscreenCanvas = (): boolean =>
  typeof OffscreenCanvas !== 'undefined' &&
  typeof OffscreenCanvas.prototype.getContext === 'function';

export const createCanvas = (width: number, height: number): CanvasHandle => {
  const safeWidth = Math.max(1, Math.round(width));
  const safeHeight = Math.max(1, Math.round(height));
  if (supportsOffscreenCanvas()) {
    const canvas = new OffscreenCanvas(safeWidth, safeHeight);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Не удалось создать OffscreenCanvas 2D-контекст.');
    return { canvas, ctx };
  }
  if (typeof document === 'undefined') {
    throw new Error('Нет доступного canvas-контекста для работы с изображением.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = safeWidth;
  canvas.height = safeHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Не удалось создать canvas 2D-контекст.');
  return { canvas, ctx };
};

/** Draws a bitmap into a canvas and reads it back as a pixel buffer. */
export const bitmapToPixelBuffer = (bitmap: ImageBitmap): PixelBuffer => {
  const { ctx } = createCanvas(bitmap.width, bitmap.height);
  ctx.drawImage(bitmap, 0, 0);
  const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  return {
    data: imageData.data,
    width: bitmap.width,
    height: bitmap.height,
    channels: 4,
  };
};

export interface DecodedImage {
  bitmap: ImageBitmap;
  sniff: SniffResult;
  width: number;
  height: number;
  sizeBytes: number;
  declaredMimeType?: string;
  fileName?: string;
}

export interface DecodeGuardOptions {
  maxBytes: number;
  maxPixels: number;
  /** Formats the validator may decode at all. */
  allowedFormats: readonly string[];
}

export const DEFAULT_DECODE_GUARDS: DecodeGuardOptions = {
  maxBytes: 25 * 1024 * 1024,
  maxPixels: 40_000_000,
  allowedFormats: ['jpeg', 'png', 'webp', 'bmp', 'heic'],
};

export class ImageRejectedError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'ImageRejectedError';
    this.code = code;
  }
}

/**
 * Sniffs the file (magic bytes, not the declared type), enforces the size and
 * decompression-bomb guards, then decodes it.
 *
 * SVG and HTML never reach the decoder; animated and truncated files are
 * rejected with a human-readable reason.
 */
export const decodeImageFile = async (
  file: Blob,
  options: Partial<DecodeGuardOptions> & { fileName?: string; declaredMimeType?: string } = {},
): Promise<DecodedImage> => {
  const guards = { ...DEFAULT_DECODE_GUARDS, ...options };

  if (file.size > guards.maxBytes) {
    throw new ImageRejectedError(
      'file_too_large',
      `Файл слишком большой: ${(file.size / (1024 * 1024)).toFixed(1)} МБ (предел ${(guards.maxBytes / (1024 * 1024)).toFixed(0)} МБ).`,
    );
  }
  if (file.size < 64) {
    throw new ImageRejectedError('file_too_small', 'Файл слишком мал, чтобы быть изображением.');
  }

  const head = new Uint8Array(await file.slice(0, 65536).arrayBuffer());
  const sniff = sniffImage(head);

  if (!sniff.supported) {
    throw new ImageRejectedError(
      'unsupported_format',
      sniff.reason ?? 'Формат файла не поддерживается.',
    );
  }
  if (!guards.allowedFormats.includes(sniff.format)) {
    throw new ImageRejectedError(
      'format_not_allowed',
      `Формат ${sniff.format.toUpperCase()} не принимается этим валидатором.`,
    );
  }
  if (sniff.malformed) {
    throw new ImageRejectedError(
      'malformed',
      sniff.reason ?? 'Файл повреждён: не удалось прочитать размеры изображения.',
    );
  }
  if (sniff.width && sniff.height && sniff.width * sniff.height > guards.maxPixels) {
    throw new ImageRejectedError(
      'too_many_pixels',
      `Слишком большое разрешение: ${sniff.width}×${sniff.height}.`,
    );
  }

  const bitmap = await decodeBitmap(file);
  return {
    bitmap,
    sniff,
    width: bitmap.width,
    height: bitmap.height,
    sizeBytes: file.size,
    ...(options.declaredMimeType ? { declaredMimeType: options.declaredMimeType } : {}),
    ...(options.fileName ? { fileName: options.fileName } : {}),
  };
};

/** `createImageBitmap` with EXIF-aware orientation, with a graceful fallback. */
export const decodeBitmap = async (blob: Blob): Promise<ImageBitmap> => {
  try {
    return await createImageBitmap(blob, { imageOrientation: 'from-image' });
  } catch {
    try {
      return await createImageBitmap(blob);
    } catch {
      throw new ImageRejectedError(
        'decode_failed',
        'Не удалось декодировать изображение: возможно, файл повреждён.',
      );
    }
  }
};
